/**
 * Freehand Drawing Tool - React Component
 * Main component that integrates all freehand functionality
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { FreehandTool } from './FreehandTool';
import { SnappingEngine, createSnapIndicators } from './snapping';
import { pathToSvgElement, renderPathNodes, renderSnapIndicator } from './rendering';

const FreehandComponent = ({ 
  isActive = false,
  config = {
    snap: {
      enabled: true,
      grid: true,
      angles: true,
      objects: true,
      tolerance: 8,
      angleStep: 45
    },
    smoothing: {
      enabled: true,
      tolerance: 2.0,
      maxError: 2.0,
      minDistance: 2,
      minTime: 8
    },
    defaultStroke: {
      color: '#000000',
      width: 2,
      lineCap: 'round',
      lineJoin: 'round',
      opacity: 1
    },
    showHandles: true,
    showNodes: true,
    autoClose: true,
    closeThreshold: 10
  },
  onPathCreated = () => {},
  onPathUpdated = () => {},
  onPathFinished = () => {},
  canvasSize = { width: 800, height: 600 },
  zoom = 1,
  pan = { x: 0, y: 0 }
}) => {
  const svgRef = useRef(null);
  const toolRef = useRef(null);
  const snappingEngineRef = useRef(null);
  
  const [paths, setPaths] = useState(new Map());
  const [previewPath, setPreviewPath] = useState(null);
  const [snapIndicators, setSnapIndicators] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);

  // Initialize tool and snapping engine
  useEffect(() => {
    // Only initialize if we have valid config
    if (!config || !config.defaultStroke) {
      console.warn('FreehandComponent: Invalid config provided');
      return;
    }

    if (!toolRef.current) {
      try {
        toolRef.current = new FreehandTool(config);

        // Set up event listeners
        toolRef.current.addEventListener((event) => {
        switch (event.type) {
          case 'pathStarted':
            setIsDrawing(true);
            onPathCreated(event.path);
            break;
            
          case 'pathUpdated':
            setPaths(prev => new Map(prev.set(event.pathId, event.path)));
            onPathUpdated(event.path);
            break;
            
          case 'pathFinished':
            setIsDrawing(false);
            // Don't store the path in component state since it becomes a template element
            setPreviewPath(null);
            onPathFinished(event.path);
            break;
            
          case 'pathCancelled':
            setIsDrawing(false);
            setPaths(prev => {
              const newPaths = new Map(prev);
              newPaths.delete(event.pathId);
              return newPaths;
            });
            setPreviewPath(null);
            break;
        }
      });
      } catch (error) {
        console.error('Failed to initialize FreehandTool:', error);
        return;
      }
    }

    if (!snappingEngineRef.current && canvasSize) {
      try {
        snappingEngineRef.current = new SnappingEngine(
          config.snap,
          { x: 0, y: 0, width: canvasSize.width, height: canvasSize.height }
        );
      } catch (error) {
        console.error('Failed to initialize SnappingEngine:', error);
      }
    }

    return () => {
      // Cleanup would go here
    };
  }, [config, canvasSize]);

  // Update tool config when props change
  useEffect(() => {
    if (toolRef.current) {
      toolRef.current.updateConfig(config);
    }
    if (snappingEngineRef.current) {
      snappingEngineRef.current.updateConfig(config.snap);
    }
  }, [config]);

  // Update preview path
  useEffect(() => {
    if (toolRef.current) {
      const preview = toolRef.current.getPreviewPath();
      setPreviewPath(preview);
    }
  });

  // Convert screen coordinates to world coordinates
  const screenToWorld = useCallback((screenPoint) => {
    // Safety checks
    if (!screenPoint || typeof screenPoint.x !== 'number' || typeof screenPoint.y !== 'number') {
      console.warn('Invalid screenPoint in screenToWorld:', screenPoint);
      return { x: 0, y: 0 };
    }

    if (typeof zoom !== 'number' || zoom === 0 || isNaN(zoom)) {
      console.warn('Invalid zoom in screenToWorld:', zoom);
      return { x: screenPoint.x, y: screenPoint.y };
    }

    return {
      x: (screenPoint.x - (pan.x || 0)) / zoom,
      y: (screenPoint.y - (pan.y || 0)) / zoom
    };
  }, [zoom, pan]);

  // Get pointer position from event
  const getPointerPosition = useCallback((event) => {
    // Safety check for SVG ref
    if (!svgRef.current) {
      console.warn('SVG ref is null in getPointerPosition');
      return { x: 0, y: 0 };
    }

    const rect = svgRef.current.getBoundingClientRect();
    if (!rect) {
      console.warn('Could not get bounding rect');
      return { x: 0, y: 0 };
    }

    // Safety check for event properties
    const clientX = typeof event.clientX === 'number' ? event.clientX : 0;
    const clientY = typeof event.clientY === 'number' ? event.clientY : 0;

    const screenPoint = {
      x: clientX - rect.left,
      y: clientY - rect.top
    };

    // Validate screen point
    if (typeof screenPoint.x !== 'number' || typeof screenPoint.y !== 'number' ||
        isNaN(screenPoint.x) || isNaN(screenPoint.y)) {
      console.warn('Invalid screen point:', screenPoint);
      return { x: 0, y: 0 };
    }

    return screenToWorld(screenPoint);
  }, [screenToWorld]);

  // Event handlers
  const handlePointerDown = useCallback((event) => {
    if (!isActive || !toolRef.current || !svgRef.current) return;

    event.preventDefault();

    // Capture pointer for true freehand drawing
    if (svgRef.current.setPointerCapture) {
      svgRef.current.setPointerCapture(event.pointerId);
    }

    const point = getPointerPosition(event);
    if (point && typeof point.x === 'number' && typeof point.y === 'number') {
      toolRef.current.onPointerDown(point);
      setIsDrawing(true);
    }
  }, [isActive, getPointerPosition]);

  const handlePointerMove = useCallback((event) => {
    if (!isActive || !toolRef.current || !svgRef.current) return;

    const point = getPointerPosition(event);
    if (point && typeof point.x === 'number' && typeof point.y === 'number') {
      // Only call onPointerMove if we're actively drawing
      if (isDrawing) {
        toolRef.current.onPointerMove(point);

        // Update snap indicators
        if (snappingEngineRef.current) {
          const snapResult = snappingEngineRef.current.snap(point);
          const indicators = createSnapIndicators(snapResult);
          setSnapIndicators(indicators);
        }
      }
    }
  }, [isActive, getPointerPosition, isDrawing]);

  const handlePointerUp = useCallback((event) => {
    if (!isActive || !toolRef.current || !svgRef.current) return;

    // Release pointer capture
    if (svgRef.current.releasePointerCapture) {
      svgRef.current.releasePointerCapture(event.pointerId);
    }

    const point = getPointerPosition(event);
    if (point && typeof point.x === 'number' && typeof point.y === 'number') {
      toolRef.current.onPointerUp(point);
    }
    setSnapIndicators([]);
    setIsDrawing(false);
  }, [isActive, getPointerPosition]);

  const handleDoubleClick = useCallback((event) => {
    if (!isActive || !toolRef.current || !svgRef.current) return;

    const point = getPointerPosition(event);
    if (point && typeof point.x === 'number' && typeof point.y === 'number') {
      toolRef.current.onDoubleClick(point);
    }
  }, [isActive, getPointerPosition]);

  const handleKeyDown = useCallback((event) => {
    if (!isActive || !toolRef.current) return;
    toolRef.current.onKeyDown(event);
  }, [isActive]);

  const handleKeyUp = useCallback((event) => {
    if (!isActive || !toolRef.current) return;
    toolRef.current.onKeyUp(event);
  }, [isActive]);

  // Set up keyboard listeners
  useEffect(() => {
    if (isActive) {
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
      
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
      };
    }
  }, [isActive, handleKeyDown, handleKeyUp]);

  // Render paths to SVG elements
  const renderPaths = () => {
    const elements = [];
    const markers = [];

    // Render completed paths
    Array.from(paths.values()).forEach(path => {
      const { pathElement, markers: pathMarkers } = pathToSvgElement(path);
      elements.push(
        <g key={path.id} data-path-id={path.id}>
          <g dangerouslySetInnerHTML={{ __html: pathElement }} />
          {config.showNodes && (
            <g dangerouslySetInnerHTML={{ 
              __html: renderPathNodes(path, new Set()) 
            }} />
          )}
        </g>
      );
      if (pathMarkers) {
        markers.push(pathMarkers);
      }
    });

    // Render preview path
    if (previewPath) {
      const { pathElement } = pathToSvgElement(previewPath, false);
      elements.push(
        <g key="preview" className="preview-path">
          <g dangerouslySetInnerHTML={{ __html: pathElement }} />
        </g>
      );
    }

    return { elements, markers };
  };

  const { elements: pathElements, markers } = renderPaths();

  // Don't render if essential props are missing
  if (!canvasSize || typeof canvasSize.width !== 'number' || typeof canvasSize.height !== 'number') {
    console.warn('FreehandComponent: Invalid canvasSize', canvasSize);
    return null;
  }

  return (
    <div className="freehand-tool">
      <svg
        ref={svgRef}
        width={canvasSize.width}
        height={canvasSize.height}
        viewBox={`0 0 ${canvasSize.width} ${canvasSize.height}`}
        style={{
          cursor: isActive ? (isDrawing ? 'crosshair' : 'crosshair') : 'default',
          userSelect: 'none'
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onDoubleClick={handleDoubleClick}
      >
        {/* Definitions for markers */}
        <defs dangerouslySetInnerHTML={{ __html: markers.join('\n') }} />
        
        {/* Grid (if enabled) */}
        {config.snap.grid && (
          <g className="grid" opacity="0.3">
            {/* Grid lines would be rendered here */}
          </g>
        )}
        
        {/* Path elements */}
        <g className="paths" transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {pathElements}
        </g>
        
        {/* Snap indicators */}
        {snapIndicators.map((indicator, index) => (
          <g key={index} dangerouslySetInnerHTML={{ 
            __html: renderSnapIndicator(indicator.position, indicator.metadata?.snapType || 'default')
          }} />
        ))}
      </svg>

      {/* Tool status */}
      {isActive && (
        <div className="freehand-status" style={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          pointerEvents: 'none'
        }}>
          {isDrawing ? (
            <div>
              <div>Drawing path... Double-click or press Enter to finish</div>
              <div>Hold Ctrl for angle snap, Shift for orthogonal, Alt for precision</div>
            </div>
          ) : (
            <div>Click to start drawing, drag for freehand curves</div>
          )}
        </div>
      )}

      {/* CSS for preview path styling */}
      <style jsx>{`
        .preview-path {
          opacity: 0.6;
        }
        .preview-path path {
          stroke-dasharray: 4 4;
        }
        .freehand-tool {
          position: relative;
          width: 100%;
          height: 100%;
        }
      `}</style>
    </div>
  );
};

// Export both the component and utility functions
export { FreehandTool } from './FreehandTool';
export { SnappingEngine } from './snapping';
export * from './types';
export * from './geometry';
export * from './rendering';

export default FreehandComponent;
